import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  User,
  Calendar,
  Heart,
  LogOut,
  Bell,
  ChevronRight,
  Star,
  Users,
  Cake,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { MemberQuickNav } from '../MemberQuickNav';
import { ParadeSignupCard } from '../ParadeSignupCard';
import { HolidayInviteCard } from '../HolidayInviteCard';
import { useAuth } from '../../context/AuthContext';
import api, { formatApiErrorDetail } from '../../lib/api';
import { toast } from 'sonner';

const LogHoursDialog = ({ open, onOpenChange, onLogged }) => {
  const [activity, setActivity] = useState('');
  const [hours, setHours] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.post('/volunteer/hours', { activity, hours: Number(hours), date });
      toast.success('Volunteer hours logged. Thank you for your service!');
      onOpenChange(false);
      setActivity('');
      setHours('');
      onLogged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="log-hours-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-[#3c4a2a]">Log Volunteer Hours</DialogTitle>
          <DialogDescription>Record your service to the community.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="vh-activity" className="text-[#3c4a2a]">Activity *</Label>
            <Input
              id="vh-activity"
              value={activity}
              onChange={(e) => setActivity(e.target.value)}
              required
              data-testid="log-hours-activity-input"
              className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
              placeholder="e.g., Event setup, tutoring session"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="vh-hours" className="text-[#3c4a2a]">Hours *</Label>
              <Input
                id="vh-hours"
                type="number"
                min="0.5"
                max="100"
                step="0.5"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                required
                data-testid="log-hours-hours-input"
                className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
              />
            </div>
            <div>
              <Label htmlFor="vh-date" className="text-[#3c4a2a]">Date *</Label>
              <Input
                id="vh-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                data-testid="log-hours-date-input"
                className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={isSubmitting}
            data-testid="log-hours-submit-btn"
            className="w-full bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white py-5"
          >
            {isSubmitting ? 'Saving...' : 'Log Hours'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const RenewalBanner = ({ daysToRenewal, expiresAt }) => (
  <div
    data-testid="renewal-reminder-banner"
    className={`mb-8 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 ${
      daysToRenewal < 0 ? 'bg-[#d84f5b]/10 border border-[#d84f5b]/30' : 'bg-[#bd8cae]/20 border border-[#bd8cae]/40'
    }`}
  >
    <div>
      <p className="font-medium text-[#3c4a2a]">
        {daysToRenewal < 0
          ? 'Your membership has lapsed.'
          : `Your membership renews in ${daysToRenewal} day${daysToRenewal === 1 ? '' : 's'} — on ${expiresAt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.`}
      </p>
      <p className="text-sm text-[#5f6d51]/80 mt-1">
        Renew today to keep your member benefits without interruption.
      </p>
    </div>
    <Link to="/membership">
      <Button data-testid="renewal-reminder-renew-btn" className="bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white whitespace-nowrap">
        Renew Membership
      </Button>
    </Link>
  </div>
);

const QuickStats = ({ user, rsvpCount, volunteerTotal }) => (
  <div className="grid sm:grid-cols-3 gap-4">
    <Card className="bg-white border-0 shadow-md">
      <CardContent className="p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-[#bd8cae]/30 flex items-center justify-center mx-auto mb-3">
          <Star className="w-6 h-6 text-[#bd8cae]" />
        </div>
        <p className="font-serif text-2xl text-[#3c4a2a]" data-testid="dashboard-membership-level">
          {user.membership_level || 'Member'}
        </p>
        <p className="text-sm text-[#3c4a2a]/60">Membership Level</p>
      </CardContent>
    </Card>
    <Card className="bg-white border-0 shadow-md">
      <CardContent className="p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-[#9dad84]/30 flex items-center justify-center mx-auto mb-3">
          <Calendar className="w-6 h-6 text-[#5d6b3c]" />
        </div>
        <p className="font-serif text-2xl text-[#3c4a2a]" data-testid="dashboard-rsvp-count">
          {rsvpCount}
        </p>
        <p className="text-sm text-[#3c4a2a]/60">Event RSVPs</p>
      </CardContent>
    </Card>
    <Card className="bg-white border-0 shadow-md">
      <CardContent className="p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-[#5d6b3c]/20 flex items-center justify-center mx-auto mb-3">
          <Heart className="w-6 h-6 text-[#5d6b3c]" />
        </div>
        <p className="font-serif text-2xl text-[#3c4a2a]" data-testid="dashboard-volunteer-hours">
          {volunteerTotal}
        </p>
        <p className="text-sm text-[#3c4a2a]/60">Volunteer Hours</p>
      </CardContent>
    </Card>
  </div>
);

const UpcomingEventsCard = ({ rsvps }) => (
  <Card className="bg-white border-0 shadow-md">
    <CardHeader className="flex flex-row items-center justify-between">
      <CardTitle className="font-serif text-xl text-[#3c4a2a]">
        Your Upcoming Events
      </CardTitle>
      <Link to="/events">
        <Button variant="link" className="text-[#5d6b3c] p-0" data-testid="dashboard-view-events-btn">
          Browse Events
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </Link>
    </CardHeader>
    <CardContent>
      {rsvps.length === 0 ? (
        <div className="text-center py-8" data-testid="dashboard-no-rsvps">
          <Calendar className="w-12 h-12 text-[#bd8cae] mx-auto mb-3" />
          <p className="text-[#3c4a2a]/60 mb-4">
            You haven't RSVP'd to any events yet.
          </p>
          <Link to="/events">
            <Button className="bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white">
              Explore Upcoming Events
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {rsvps.map((rsvp) => {
            const event = rsvp.event || {};
            const eventDate = event.date ? new Date(`${event.date}T12:00:00`) : null;
            return (
              <div
                key={rsvp.id}
                data-testid={`dashboard-rsvp-${rsvp.id}`}
                className="flex items-center p-4 rounded-lg bg-[#f1e3eb] hover:bg-[#bd8cae]/20 transition-colors"
              >
                <div className="w-14 h-14 rounded-lg bg-[#5d6b3c] text-white flex flex-col items-center justify-center mr-4">
                  <span className="text-lg font-bold leading-none">
                    {eventDate ? eventDate.getDate() : '—'}
                  </span>
                  <span className="text-xs uppercase">
                    {eventDate ? eventDate.toLocaleString('default', { month: 'short' }) : ''}
                  </span>
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-[#3c4a2a]">
                    {event.title || rsvp.event_title}
                  </h4>
                  <p className="text-sm text-[#3c4a2a]/60">
                    {event.time} {event.location ? `• ${event.location}` : ''}
                    {rsvp.guests > 0 ? ` • +${rsvp.guests} guest${rsvp.guests > 1 ? 's' : ''}` : ''}
                  </p>
                </div>
                <Badge className="bg-[#9dad84]/30 text-[#5d6b3c]">
                  Registered
                </Badge>
              </div>
            );
          })}
        </div>
      )}
    </CardContent>
  </Card>
);

const MemberResourcesCard = ({ onOpenDirectory }) => {
  const resources = [
    { title: 'Welcome Packet', icon: Star, description: 'Your founding member next steps', to: '/welcome', testId: 'resource-welcome-packet' },
    { title: 'Member Directory', icon: Users, description: 'Connect with fellow members', onClick: onOpenDirectory, testId: 'resource-member-directory' },
    { title: 'Leadership Library', icon: Heart, description: 'Access exclusive content', to: '/leadership-library', testId: 'resource-leadership-library' },
    { title: 'Event Recordings', icon: Calendar, description: 'Watch past events' },
    { title: 'My Profile', icon: User, description: 'Update your information' },
  ];
  return (
    <Card className="bg-white border-0 shadow-md">
      <CardHeader>
        <CardTitle className="font-serif text-xl text-[#3c4a2a]">
          Member Resources
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid sm:grid-cols-2 gap-4">
          {resources.map((resource) => {
            const inner = (
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-[#bd8cae]/30 flex items-center justify-center mr-3">
                  <resource.icon className="w-5 h-5 text-[#5d6b3c]" />
                </div>
                <div>
                  <h4 className="font-medium text-[#3c4a2a]">
                    {resource.title}
                  </h4>
                  <p className="text-xs text-[#3c4a2a]/60">
                    {resource.description}
                  </p>
                </div>
              </div>
            );
            const cardClass = 'p-4 rounded-lg border border-[#bd8cae]/30 hover:border-[#5d6b3c] hover:shadow-md transition-all cursor-pointer block';
            if (resource.to) {
              return (
                <Link key={resource.title} to={resource.to} data-testid={resource.testId} className={cardClass}>
                  {inner}
                </Link>
              );
            }
            return (
              <div
                key={resource.title}
                onClick={resource.onClick}
                data-testid={resource.testId}
                className={cardClass}
              >
                {inner}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const MembershipCard = ({ user, expiresAt }) => (
  <Card className="bg-gradient-to-br from-[#5d6b3c] to-[#3c4a2a] border-0 text-white">
    <CardContent className="p-6">
      <div className="flex justify-between items-start mb-6">
        <div>
          <p className="text-white/70 text-sm">Membership</p>
          <p className="font-serif text-xl">{user.membership_level || 'Member'}</p>
          {user.title && (
            <p className="text-[#f1e3eb] text-sm mt-0.5" data-testid="member-title">{user.title}</p>
          )}
          {user.founding_member && (
            <p className="text-[#f1e3eb] text-sm mt-1 flex items-center gap-1" data-testid="founding-member-badge">
              <Star className="w-3.5 h-3.5" />
              Founding Member{user.founding_number ? ` #${user.founding_number}` : ''}
            </p>
          )}
        </div>
        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
          <Star className="w-5 h-5" />
        </div>
      </div>
      <div className="border-t border-white/20 pt-4">
        <p className="text-white/70 text-sm mb-1">Member ID</p>
        <p className="font-mono" data-testid="dashboard-member-id">{user.member_id || '—'}</p>
      </div>
      <div className="mt-4">
        <p className="text-white/70 text-sm mb-1">Email</p>
        <p className="text-sm">{user.email}</p>
      </div>
      <div className="mt-4">
        <p className="text-white/70 text-sm mb-1">Renewal</p>
        <p className="text-sm" data-testid="dashboard-renewal-date">
          {expiresAt
            ? `Renews ${expiresAt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
            : 'No paid membership yet'}
        </p>
      </div>
      <Link to="/membership" className="block mt-6">
        <Button className="w-full bg-white text-[#5d6b3c] hover:bg-white/90">
          View Membership Levels
        </Button>
      </Link>
    </CardContent>
  </Card>
);

const AgendaCard = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/next-meeting-agenda').then((res) => setData(res.data)).catch(() => {});
  }, []);

  if (!data?.agenda || !data.event) return null;
  const d = new Date(`${data.event.date}T12:00:00`);

  return (
    <Card className="bg-white border border-[#bd8cae]/30 shadow-md" data-testid="agenda-card">
      <CardHeader>
        <CardTitle className="font-serif text-lg text-[#3c4a2a] flex items-center gap-2">
          <Bell className="w-5 h-5 text-[#a3688f]" />
          Next Meeting Agenda
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm font-medium text-[#3c4a2a]">{data.event.title}</p>
        <p className="text-xs text-[#3c4a2a]/60 mb-3">
          {d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} · {data.event.time}
        </p>
        <div className="bg-[#f1e3eb]/60 rounded-lg p-4 text-sm text-[#3c4a2a]/85 whitespace-pre-wrap" data-testid="agenda-text">
          {data.agenda}
        </div>
      </CardContent>
    </Card>
  );
};

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const BirthdaysCard = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/members/birthdays').then((res) => setData(res.data)).catch(() => {});
  }, []);

  if (!data || !data.birthdays?.length) return null;
  const monthName = MONTH_NAMES[data.month - 1];

  return (
    <Card className="bg-white border border-[#bd8cae]/30 shadow-md" data-testid="birthdays-card">
      <CardHeader>
        <CardTitle className="font-serif text-lg text-[#3c4a2a] flex items-center gap-2">
          <Cake className="w-5 h-5 text-[#a3688f]" />
          {monthName} Birthdays
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {data.birthdays.map((b, i) => (
          <div key={`${b.name}-${b.day}`} className="flex items-center justify-between bg-[#f1e3eb]/60 rounded-lg px-4 py-2" data-testid={`birthday-item-${i}`}>
            <span className="text-[#3c4a2a] font-medium">{b.name}</span>
            <span className="text-sm text-[#a3688f]">{monthName.slice(0, 3)} {b.day}</span>
          </div>
        ))}
        <p className="text-xs text-[#3c4a2a]/50 pt-1">Wish them a happy birthday at the next meeting! 🎂</p>
      </CardContent>
    </Card>
  );
};

const QuickActionsCard = ({ onLogHours }) => (
  <Card className="bg-white border-0 shadow-md">
    <CardHeader>
      <CardTitle className="font-serif text-lg text-[#3c4a2a]">
        Quick Actions
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">
      <Link to="/member-calendar" className="block">
        <Button
          variant="outline"
          data-testid="dashboard-calendar-btn"
          className="w-full justify-start border-[#bd8cae]/50 text-[#3c4a2a] hover:bg-[#bd8cae]/20"
        >
          <Calendar className="w-4 h-4 mr-2" />
          Club Calendar
        </Button>
      </Link>
      <Link to="/member-info" className="block">
        <Button
          variant="outline"
          data-testid="dashboard-info-form-btn"
          className="w-full justify-start border-[#bd8cae]/50 text-[#3c4a2a] hover:bg-[#bd8cae]/20"
        >
          <Calendar className="w-4 h-4 mr-2" />
          My Info Form
        </Button>
      </Link>
      <Link to="/book-club" className="block">
        <Button
          variant="outline"
          data-testid="dashboard-book-club-btn"
          className="w-full justify-start border-[#bd8cae]/50 text-[#3c4a2a] hover:bg-[#bd8cae]/20"
        >
          <Star className="w-4 h-4 mr-2" />
          Book Club Corner
        </Button>
      </Link>
      <Link to="/meeting-minutes" className="block">
        <Button
          variant="outline"
          data-testid="dashboard-minutes-btn"
          className="w-full justify-start border-[#bd8cae]/50 text-[#3c4a2a] hover:bg-[#bd8cae]/20"
        >
          <Calendar className="w-4 h-4 mr-2" />
          Meeting Minutes
        </Button>
      </Link>
      <Link to="/events" className="block">
        <Button
          variant="outline"
          className="w-full justify-start border-[#bd8cae]/50 text-[#3c4a2a] hover:bg-[#bd8cae]/20"
        >
          <Calendar className="w-4 h-4 mr-2" />
          RSVP to Event
        </Button>
      </Link>
      <Button
        variant="outline"
        onClick={onLogHours}
        data-testid="dashboard-log-hours-btn"
        className="w-full justify-start border-[#bd8cae]/50 text-[#3c4a2a] hover:bg-[#bd8cae]/20"
      >
        <Heart className="w-4 h-4 mr-2" />
        Log Volunteer Hours
      </Button>
      <Link to="/get-involved#volunteer" className="block">
        <Button
          variant="outline"
          className="w-full justify-start border-[#bd8cae]/50 text-[#3c4a2a] hover:bg-[#bd8cae]/20"
        >
          <Heart className="w-4 h-4 mr-2" />
          Volunteer Opportunities
        </Button>
      </Link>
      <Link to="/donate" className="block">
        <Button
          variant="outline"
          className="w-full justify-start border-[#bd8cae]/50 text-[#3c4a2a] hover:bg-[#bd8cae]/20"
        >
          <Star className="w-4 h-4 mr-2" />
          Make a Donation
        </Button>
      </Link>
    </CardContent>
  </Card>
);

const AnnouncementsCard = () => (
  <Card className="bg-[#f1e3eb] border-0">
    <CardHeader>
      <CardTitle className="font-serif text-lg text-[#3c4a2a] flex items-center">
        <Bell className="w-5 h-5 mr-2 text-[#bd8cae]" />
        Announcements
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="p-3 bg-white rounded-lg">
        <p className="text-sm text-[#3c4a2a] font-medium">
          New leadership workshop available
        </p>
        <p className="text-xs text-[#3c4a2a]/60 mt-1">
          Sign up for our upcoming workshop series.
        </p>
      </div>
      <div className="p-3 bg-white rounded-lg">
        <p className="text-sm text-[#3c4a2a] font-medium">
          Fall Fundraiser Gala tickets open
        </p>
        <p className="text-xs text-[#3c4a2a]/60 mt-1">
          Reserve your spot for our signature October event.
        </p>
      </div>
    </CardContent>
  </Card>
);

const MemberDashboard = () => {
  const { user, logout } = useAuth();
  const [rsvps, setRsvps] = useState([]);
  const [volunteer, setVolunteer] = useState({ entries: [], total: 0 });
  const [hoursDialogOpen, setHoursDialogOpen] = useState(false);
  const navigate = useNavigate();

  const loadVolunteer = useCallback(() => {
    api.get('/volunteer/hours/me').then((res) => setVolunteer(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/rsvps/me').then((res) => setRsvps(res.data || [])).catch(() => {});
    loadVolunteer();
  }, [loadVolunteer]);

  const handleLogout = async () => {
    await logout();
    toast.success('You have been logged out');
    navigate('/');
  };

  if (!user) return null;

  const fullName = `${user.first_name} ${user.last_name}`.trim() || 'Member';
  const initials = fullName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();
  const memberSince = user.created_at ? new Date(user.created_at).getFullYear() : new Date().getFullYear();

  const expiresAt = user.membership_expires_at ? new Date(user.membership_expires_at) : null;
  const daysToRenewal = expiresAt ? Math.ceil((expiresAt - new Date()) / (1000 * 60 * 60 * 24)) : null;
  const renewalSoon = daysToRenewal !== null && daysToRenewal <= 30;

  return (
    <main className="min-h-screen pt-52 bg-[#f1e3eb]" data-testid="member-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <MemberQuickNav />
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-12">
          <div className="flex items-center mb-6 md:mb-0">
            <Avatar className="w-16 h-16 mr-4">
              <AvatarImage src="" />
              <AvatarFallback className="bg-[#5d6b3c] text-white text-xl font-serif">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="font-serif text-2xl text-[#3c4a2a]" data-testid="dashboard-welcome-heading">
                Welcome back, {fullName}!
              </h1>
              <p className="text-[#3c4a2a]/60 text-sm">Member since {memberSince}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              onClick={handleLogout}
              data-testid="dashboard-logout-btn"
              className="text-[#bd8cae] hover:text-[#a3688f] hover:bg-[#bd8cae]/10"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {renewalSoon && <RenewalBanner daysToRenewal={daysToRenewal} expiresAt={expiresAt} />}

        {/* Dashboard Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <QuickStats user={user} rsvpCount={rsvps.length} volunteerTotal={volunteer.total} />
            <UpcomingEventsCard rsvps={rsvps} />
            <ParadeSignupCard />
            <MemberResourcesCard onOpenDirectory={() => navigate('/member-directory')} />
          </div>

          <div className="space-y-8">
            <MembershipCard user={user} expiresAt={expiresAt} />
            <AgendaCard />
            <BirthdaysCard />
            <HolidayInviteCard />
            <QuickActionsCard onLogHours={() => setHoursDialogOpen(true)} />
            <AnnouncementsCard />
          </div>
        </div>
      </div>

      <LogHoursDialog
        open={hoursDialogOpen}
        onOpenChange={setHoursDialogOpen}
        onLogged={loadVolunteer}
      />
    </main>
  );
};

export default MemberDashboard;
